import React, { useEffect, useState } from 'react';
import { Select, message } from 'antd';
import { getFirestore, collection, query, where, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import SmallModal from '../../components/Components/Modals/SmallModal';
import { showSuccessNotification, showWarningNotification, showErrorNotification, showInfoNotification } from '../../utils/notifications';
import { LogVariantService } from '../services/Utils/LogVariantService';
import { NotificationService } from '../services/Utils/NotificationService';
import { useParams } from 'react-router-dom';

const { Option } = Select;

const VariantsAssignedToModal = ({ isVisible, onCancel, variants, onSuccess }) => {
    const db = getFirestore();
    const [modellistaProfiles, setModellistaProfiles] = useState([]);
    const [selectedAssignee, setSelectedAssignee] = useState(null);
    const [isAssigning, setIsAssigning] = useState(false);
    const { brandId, productId } = useParams();

    useEffect(() => {
        if (isVisible) {
            const fetchModellisti = async () => {
                try {
                    const q = query(collection(db, "Profiles"), where("role", "in", ["Modellista", "ModellerSupervisor"]));
                    const querySnapshot = await getDocs(q);
                    const profiles = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    setModellistaProfiles(profiles);
                } catch (error) {
                    console.error("Error in fetching modellers:", error);
                    showErrorNotification("Error", "Impossible to load the list of modellers");
                }
            };
            fetchModellisti();
        } else {
            // Reset dello stato quando la modale si chiude
            setSelectedAssignee(null);
        }
    }, [isVisible, db]);

    const handleAssign = async () => {
        if (!selectedAssignee) {
            showWarningNotification("Attention", "Select a modeller.");
            return;
        }

        if (!variants || variants.length === 0) {
            showWarningNotification("Attention", "No variants to assign.");
            return;
        }

        setIsAssigning(true);

        try {
            const timestamp = new Date();
            const formattedDate = timestamp.toLocaleDateString('it-IT');
            const formattedTime = timestamp.toLocaleTimeString('it-IT');
            const assignedDateString = `${formattedDate}, ${formattedTime}`;

            // Verifica se l'utente ha accesso al brand
            let brandAccessGranted = false;

            // Trova il profilo dell'utente selezionato
            const userQuery = query(collection(db, "Profiles"), where("_email", "==", selectedAssignee));
            const userQuerySnapshot = await getDocs(userQuery);

            if (!userQuerySnapshot.empty) {
                const userDoc = userQuerySnapshot.docs[0];
                const userData = userDoc.data();

                // Controlla se l'utente ha già il brand nella sua lista
                const userBrands = userData.list_main_brands || [];

                if (userBrands.includes(brandId)) {
                    brandAccessGranted = true;
                } else {
                    // Aggiungi il brand alla lista dell'utente
                    const updatedBrands = [...userBrands, brandId];
                    await updateDoc(doc(db, "Profiles", userDoc.id), {
                        list_main_brands: updatedBrands
                    });
                    brandAccessGranted = true;

                    showInfoNotification(
                        "Brand Access Granted",
                        `${userData.firstName || selectedAssignee} now has access to this brand.`
                    );
                }
            }

            // Aggiorna le varianti nel database una alla volta
            const updatedVariants = [];
            const errors = [];
            const notificationPromises = [];

            for (const variant of variants) {
                try {
                    const variantRef = doc(db, "Variants", variant.id);

                    // Aggiorna il documento della variante con i dati di assegnazione
                    await updateDoc(variantRef, {
                        assignedTo: selectedAssignee,
                        assignmentDate: assignedDateString
                    });

                    // Registra l'assegnazione nel log specifico di questa variante
                    await LogVariantService.logAssignVariant(variant.id, selectedAssignee);

                    // Crea una notifica per l'assegnazione di questa variante
                    const notificationPromise = NotificationService.createVariantAssignmentNotification(
                        variant.id,
                        brandId,
                        variant.skuModel || "",
                        selectedAssignee,
                        false,
                        variant.modelName || variant.glassesName || "",
                        productId
                    );

                    notificationPromises.push(notificationPromise);

                    // Aggiungi la variante aggiornata all'array
                    updatedVariants.push({
                        ...variant,
                        assignedTo: selectedAssignee,
                        assignmentDate: assignedDateString
                    });

                } catch (error) {
                    console.error(`Errore durante l'assegnazione della variante ${variant.id}:`, error);
                    errors.push(variant.id);
                }
            }

            // Attendi che tutte le notifiche siano create
            await Promise.all(notificationPromises);

            // Mostra notifiche appropriate in base al risultato
            if (errors.length === 0) {
                showSuccessNotification(
                    "Save successful",
                    `${updatedVariants.length} variants assigned successfully!`
                );
            } else if (errors.length < variants.length) {
                showWarningNotification(
                    "Partial assignment",
                    `${updatedVariants.length} variants assigned, but ${errors.length} have errors.`
                );
            } else {
                showErrorNotification(
                    "Error",
                    "No variants were assigned. Check the console for details."
                );
            }

            // Passa le varianti aggiornate al genitore solo se ce ne sono
            if (updatedVariants.length > 0) {
                onSuccess(updatedVariants);
            } else {
                onCancel();
            }
        } catch (error) {
            console.error("Error during variant assignment:", error);
            showErrorNotification("Error", "A general error occurred during variant assignment");
            onCancel();
        } finally {
            setIsAssigning(false);
        }
    };

    return (
        <SmallModal
            title="Assign all variants to the modeler:"
            visible={isVisible}
            onClose={onCancel}
            onConfirm={handleAssign}
            cancelText="Cancel"
            confirmText={isAssigning ? "Assegnazione in corso..." : "Assign"}
            confirmButtonProps={{ disabled: isAssigning, loading: isAssigning }}
        >
            <Select
                placeholder="Select a modeler"
                style={{ width: '100%' }}
                value={selectedAssignee}
                onChange={setSelectedAssignee}
                disabled={isAssigning}
            >
                {modellistaProfiles.map(profile => (
                    <Option key={profile.id} value={profile._email || profile.email}>
                        {profile.firstName && profile.lastName ? `${profile.firstName} ${profile.lastName}` : (profile._email || profile.email)}
                    </Option>
                ))}
            </Select>
            <div style={{ marginTop: '16px', color: '#888' }}>
                Date: {new Date().toLocaleString('it-IT')}
            </div>
            {isAssigning && (
                <div style={{ marginTop: '16px', color: '#1890ff' }}>
                    Assegnazione in corso, si prega di attendere...
                </div>
            )}
        </SmallModal>
    );
};

export default VariantsAssignedToModal;